import { Link } from "react-router-dom";

/**
 * Bloc SEO enrichi (~1200 mots) — mots-clés prioritaires ciblés :
 * marketplace Bénin, acheter en ligne au Bénin, vendre en ligne au Bénin,
 * livraison Cotonou, boutique en ligne Bénin, paiement Mobile Money Bénin,
 * application livraison Bénin, comment vendre en ligne Bénin.
 */
export const SeoAbout = () => (
  <section
    id="a-propos"
    data-testid="seo-about"
    className="py-24 md:py-28 bg-white border-y border-[#EDE7D9]"
  >
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">
        Zafara en quelques mots
      </p>
      <h2 className="mt-4 font-display text-3xl md:text-4xl text-[#16241B] leading-[1.1] tracking-tight">
        La marketplace pensée pour le Bénin —{" "}
        <span className="text-[#0E7A3B]">acheter, vendre, livrer en ligne.</span>
      </h2>

      <div className="mt-8 space-y-5 text-[#16241B]/85 text-base md:text-lg leading-relaxed">
        <p>
          <strong>Zafara</strong> est la première <strong>marketplace 100%
          béninoise</strong> qui rassemble sur une seule application les commerces
          de proximité, les vendeurs indépendants et les livreurs du pays. Que vous
          soyez à Cotonou, Porto-Novo, Parakou, Abomey-Calavi ou Bohicon, vous
          pouvez <strong>acheter en ligne au Bénin</strong> en quelques tapotements,
          payer via <strong>Mobile Money</strong> (MTN MoMo, Moov Money) et être
          livré partout dans le pays. La <strong>livraison à Cotonou</strong> est
          assurée le jour même par notre réseau de livreurs indépendants agréés.
        </p>
        <p>
          Zafara est conçue pour les <strong>vendeurs et commerçants
          locaux</strong> : boutiques de quartier, artisans, restaurateurs,
          producteurs, prestataires de services. En quelques minutes, chacun peut
          créer sa <strong>boutique en ligne au Bénin</strong>, publier ses
          produits, gérer ses commandes et toucher une clientèle bien au-delà de
          sa rue. Pas de caution, pas de frais fixes — vous ne payez que quand
          vous vendez.
        </p>
      </div>

      <h3 className="mt-14 font-display text-2xl md:text-3xl text-[#16241B] tracking-tight">
        Comment ça marche ?
      </h3>
      <ol className="mt-6 space-y-5 text-[#16241B]/85 text-base md:text-lg leading-relaxed">
        <li>
          <strong>1. Inscription en 2 minutes.</strong> Téléchargez l'application
          Zafara sur l'App Store ou Google Play, créez votre compte acheteur,
          vendeur ou livreur. Numéro Bénin obligatoire, aucune caution demandée.
        </li>
        <li>
          <strong>2. Publiez ou parcourez.</strong> Vendeurs : ajoutez vos
          produits, prix, photos et livraison possible. Acheteurs : filtrez par
          catégorie, ville et note. Chattez en direct avec le vendeur.
        </li>
        <li>
          <strong>3. Payez, livrez, notez.</strong> Choisissez Mobile Money (MTN
          MoMo ou Moov Money), cash à la livraison ou carte. Un livreur
          indépendant Zafara prend en charge la livraison, vous suivez en temps
          réel, vous notez à la fin.
        </li>
      </ol>

      <h3 className="mt-14 font-display text-2xl md:text-3xl text-[#16241B] tracking-tight">
        Pourquoi choisir Zafara plutôt qu'une autre app ?
      </h3>
      <ul className="mt-6 space-y-4 text-[#16241B]/85 text-base md:text-lg leading-relaxed">
        <li>
          <strong>100% pensée pour le Bénin.</strong> Pas un clone d'une app
          américaine, pas un fork d'une plateforme française — chaque fonctionnalité
          a été testée sur le terrain avec des commerçants de Cotonou et de
          Parakou.
        </li>
        <li>
          <strong>Optimisée 3G+.</strong> L'application est légère et rapide même
          quand le réseau est faible. Interface efficace en zone rurale, mode
          économie de données.
        </li>
        <li>
          <strong>Paiement Mobile Money natif.</strong> MTN MoMo et Moov Money
          sont intégrés dès le premier écran. Aucune redirection, aucun compte
          supplémentaire à créer.
        </li>
        <li>
          <strong>Vendeurs et livreurs indépendants gagnants.</strong> Zafara ne
          prélève une commission que sur les ventes réussies. Aucun abonnement,
          aucun frais fixe, aucune caution — les livreurs gardent 90% de leurs
          courses.
        </li>
      </ul>

      <h3 className="mt-14 font-display text-2xl md:text-3xl text-[#16241B] tracking-tight">
        Comment vendre en ligne au Bénin avec Zafara
      </h3>
      <p className="mt-6 text-[#16241B]/85 text-base md:text-lg leading-relaxed">
        <strong>Vendre en ligne au Bénin</strong> devient enfin simple. Zafara
        propose des outils pensés pour le contexte local : catalogue
        multi-produits, chat direct avec l'acheteur, calcul automatique des frais
        de livraison, paiement à la livraison, paiement Mobile Money instantané.
        C'est <strong>l'e-commerce au Bénin en 2026</strong>, taillé sur mesure —
        sans dépendre d'infrastructures pensées ailleurs. Les vendeurs les plus
        actifs remontent naturellement dans les résultats de recherche
        in-app, avec un bonus de visibilité pour les nouveaux inscrits pendant
        les 30 premiers jours.
      </p>

      <h3 className="mt-14 font-display text-2xl md:text-3xl text-[#16241B] tracking-tight">
        Témoignages
      </h3>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          {
            n: "En cours de collecte",
            v: "Les premiers témoignages seront publiés ici dès l'ouverture officielle. Les commerçants et livreurs bêta-testeurs partageront leur retour d'expérience.",
          },
          {
            n: "Bientôt",
            v: "Vous êtes vendeur, livreur ou acheteur actif sur Zafara et souhaitez partager votre expérience ? Écrivez-nous à contact@zafarabenin.fr.",
          },
        ].map((t) => (
          <div
            key={t.n}
            className="rounded-2xl border border-dashed border-[#EDE7D9] p-6 bg-[#FBFAF6]"
          >
            <p className="font-utility text-[10px] uppercase tracking-widest text-[#F5A800]">
              {t.n}
            </p>
            <p className="mt-3 text-[#16241B]/70 text-sm md:text-base leading-relaxed">
              {t.v}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-14 space-y-5 text-[#16241B]/85 text-base md:text-lg leading-relaxed">
        <p>
          Zafara est éditée par{" "}
          <Link to="/whilsco-digit" className="text-[#0E7A3B] underline underline-offset-4 hover:text-[#F5A800]">
            Whilsco-Digit SARL
          </Link>
          , un studio produit fondé par{" "}
          <Link to="/fondateur" className="text-[#0E7A3B] underline underline-offset-4 hover:text-[#F5A800]">
            Wilson Eric
          </Link>
          , ingénieur béninois. La marque a été pensée pour se décliner
          naturellement dans toute l'Afrique de l'Ouest : après le Bénin, Zafara
          arrivera au Togo, au Sénégal, en Côte d'Ivoire, au Burkina Faso et au
          Niger.
        </p>
        <p className="pt-2">
          Prêt à démarrer ?{" "}
          <Link to="/telecharger" className="text-[#0E7A3B] font-semibold underline underline-offset-4 hover:text-[#F5A800]">
            Téléchargez l'application livraison Bénin
          </Link>
          {" "}sur l'App Store ou Google Play, ou{" "}
          <Link to="/faq" className="text-[#0E7A3B] font-semibold underline underline-offset-4 hover:text-[#F5A800]">
            consultez la FAQ
          </Link>
          .
        </p>
      </div>
    </div>
  </section>
);
